import { useState } from 'react';

import DetailHeader from '../ContactDetail/DetailHeader';
import { DetailHeaderOrg, DetailHeaderOrgLoading } from '../DetailHeaderOrg';
import { OrgDataSection } from '../OrgDataSection';
import { SidebarNotes } from '../SidebarNotes';

import { OrgInteractSectionPure } from './OrgInteractSectionPure';
import { OrgSwitcherPure } from './OrgSwitcherPure';
import type { OrgDetailData, OrgDetailMutations, OrgTabId } from './types';

export type OrgDetailPageCoreProps = {
  data: OrgDetailData | null;
  isLoading: boolean;
  mutations: OrgDetailMutations;
  /** Controlled active tab. Omit (with onTabChange) to use internal state. */
  activeTab?: OrgTabId;
  onTabChange?: (tab: OrgTabId) => void;
  onClose?: () => void;
  /** Opens a contact from the interact list. Platform-specific navigation. */
  onContactClick: (args: { contactId: string; cadenceId?: string }) => void;
  /** Whether a card is the active/open one (dashboard router). Omitted on extension. */
  isContactActive?: (contactId: string, cadenceId: string | undefined) => boolean;
  /** Whether a card is selected (dashboard multi-select). Omitted on extension. */
  isContactChecked?: (contactId: string, cadenceId: string | undefined) => boolean;
};

/**
 * Pure org detail page. Receives ALL data and mutations via props and makes zero
 * API calls, so the dashboard (tRPC wrapper) and the extension (vanilla client)
 * render the same UI. Mirrors the app-local OrgDetailPage composition exactly:
 * DetailHeader + DetailHeaderOrg + OrgSwitcherPure footer, then always-on notes,
 * then the interact list or the data section per the active tab.
 */
export function OrgDetailPageCore({
  data,
  isLoading,
  mutations,
  activeTab: activeTabProp,
  onTabChange,
  onClose,
  onContactClick,
  isContactActive,
  isContactChecked,
}: OrgDetailPageCoreProps) {
  const [internalTab, setInternalTab] = useState<OrgTabId>('interact');
  const activeTab = activeTabProp ?? internalTab;
  const handleTabChange = (tab: OrgTabId) => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setInternalTab(tab);
    }
  };

  if (isLoading || data == null) {
    return (
      <DetailHeader footer={<OrgSwitcherPure activeTab={activeTab} onTabChange={handleTabChange} />}>
        <DetailHeaderOrgLoading />
      </DetailHeader>
    );
  }

  return (
    <>
      <DetailHeader onClose={onClose} footer={<OrgSwitcherPure activeTab={activeTab} onTabChange={handleTabChange} />}>
        <DetailHeaderOrg
          name={data.orgPrimaryName ?? undefined}
          linkedinSlug={data.orgPrimaryFields.orgId?.data ?? undefined}
          pictureUrl={data.orgPictureUrl}
        />
      </DetailHeader>
      <div className="h-3" />
      <SidebarNotes
        recordId={data.orgId}
        title="Notes"
        initialValue={data.org.notes ?? undefined}
        onDebounceChange={mutations.updateNotes}
      />
      {activeTab === 'interact' && (
        <OrgInteractSectionPure
          contacts={data.contacts}
          contactsLoading={false}
          stages={data.stages}
          stagesLoading={false}
          stageUpdatingContactId={mutations.stageUpdatingContactId}
          isContactActive={isContactActive}
          isContactChecked={isContactChecked}
          onContactClick={onContactClick}
          onContactStageChange={mutations.setContactStage}
        />
      )}
      {activeTab === 'data' && (
        <OrgDataSection
          orgFields={data.org.fields}
          estimatedAum={data.estimatedAum}
          flags={data.flags}
          isFlagsPending={mutations.isFlagsPending}
          onSetFlag={mutations.onSetFlag}
          onSetHqState={mutations.onSetHqState}
          onAddValue={mutations.onAddValue}
          onDeleteValue={mutations.onDeleteValue}
          onMakePrimary={mutations.onMakePrimary}
          onRemovePrimary={mutations.onRemovePrimary}
        />
      )}
    </>
  );
}
